import { create } from "zustand";
import { Draft, produce } from "immer";
import { v4 as uuidv4 } from "uuid";
import useMainStore from "./mainStore";
import { saveDataStore } from "../utility/cache/manageDataStore";
import debounce from "../utility/debounce";
import useTextFilesStore from "./textFilesStore";
import { getUserAccount } from "../utility/cache/storeUserAccount";
import { removeTextFile } from "../utility/cache/manageTextFile";
import useAccountManagementStore from "./accountManagementStore";
import { isValidUUID } from "../utility/isValidUUID";

export const dataStoreDefaultData: DataStoreType["data"] = {
    notes: {},
    folders: {},
    tags: {},
    deleted: {},
};

// I put this interface here, as it is only necessary for this store.
// This makes it easier to access and edit the interface alongside the store.
// Note/Tag/etc are declared in separate d.ts files for access across my application
export interface DataStoreType {
    data: {
        notes: { [id: string]: Note };
        folders: { [id: string]: Folder };
        tags: { [id: string]: Tag };
        deleted?: Deleted;
    };

    set: {
        data: (
            fn: (
                draft: Partial<DataStoreType["data"]>
            ) => Partial<DataStoreType["data"]>
        ) => void; // Set the entire dataStore
        folder: (id: string, data: Partial<Folder>) => void; // Set a single folder
        folders: (data: { [id: string]: Folder }) => void; // Set all folders
        note: (id: string, data: Partial<Note>, skipSave?: boolean) => void; // Set a single note
        notes: (data: { [id: string]: Note }) => void; // Set all notes
        tag: (id: string, data: Tag) => void; // Set a single tag
        tags: (data: { [id: string]: Tag }) => void; // Set all tags
        // dataStore: (data: Partial<DataStoreType>) => void; // Set the entire dataStore
    };
    add: {
        folder: (data?: Partial<Folder>) => string;
        note: (data?: Partial<Note>, openAfterCreate?: boolean) => string;
        tag: (data?: Partial<Tag>) => string;
        duplicateNote: (
            id: string,
            keepFolder?: boolean,
            overrideData?: Partial<Note>
        ) => void;
    };
    remove: {
        folder: (id: string) => void;
        note: (id: string) => void;
        tag: (id: string) => void;
    };
    initialize: () => void;
}

const useDataStore = create<DataStoreType>((set, get) => ({
    data: { ...dataStoreDefaultData },

    set: {
        /**
         * Sets a single note by id in the dataStore.
         * If the note doesn't exist, it will be created.
         * If the note already exists, it will be updated.
         * @param data The note to set.
         */
        folder: (id, data: Partial<Folder>) => {
            let id_ = id;
            // If the id is not provided, create a new one
            if (!id_) {
                id_ = uuidv4();
            }

            set(
                produce((draft: Draft<DataStoreType>) => {
                    draft.data.folders[id_] = {
                        ...draft.data.folders[id_],
                        ...data,
                        updatedAt: new Date().toISOString(),
                    };
                })
            );

            const userID = getUserAccount()?.id || "default";
            saveDataStore(userID);
        },
        /**
         * Sets all notes in the dataStore.
         * Replaces any existing notes with the provided notes.
         * @param data An object containing notes keyed by their ID.
         */
        folders: (data) =>
            set(
                produce((draft: Draft<DataStoreType>) => {
                    draft.data.folders = data;
                })
            ),

        /**
         * Sets a single note by id in the dataStore.
         * If the note doesn't exist, it will be created.
         * If the note already exists, it will be updated.
         * @param data The note to set.
         */
        note: (id, data, skipSave?: boolean) => {
            set(
                produce((draft: Draft<DataStoreType>) => {
                    draft.data.notes[id] = {
                        ...draft.data.notes[id],
                        ...data,
                        updatedAt: new Date().toISOString(),
                    };
                })
            );

            if (skipSave) return;

            const userID = getUserAccount()?.id || "default";
            saveDataStore(userID);
        },
        /**
         * Sets all notes in the dataStore.
         * Replaces any existing notes with the provided notes.
         * @param data An object containing notes keyed by their ID.
         */
        notes: (data) =>
            set(
                produce((draft: Draft<DataStoreType>) => {
                    draft.data.notes = data;
                })
            ),
        /**
         * Sets a single tag by id in the dataStore.
         * If the tag doesn't exist, it will be created.
         * If the tag already exists, it will be updated.
         * @param data The tag to set.
         */
        tag: (id, data) => {
            set(
                produce((draft: Draft<DataStoreType>) => {
                    draft.data.tags[id] = data;
                })
            );

            const userID = getUserAccount()?.id || "default";
            saveDataStore(userID);
        },
        /**
         * Sets all tags in the dataStore.
         * Replaces any existing tags with the provided tags.
         * @param data An object containing tags keyed by their ID.
         */
        tags: (data) =>
            set(
                produce((draft: Draft<DataStoreType>) => {
                    draft.data.tags = data;
                })
            ),
        /**
         * Sets the entire dataStore to the given data.
         * Replaces any existing notes and tags with the provided data.
         * @param data A partial DataStoreType object containing the new notes and tags.
         */
        data: (fn) => {
            set(
                produce((draft: Draft<DataStoreType>) => {
                    // Merge the existing dataStore with the new data
                    const data = fn(draft.data);

                    // Filter out any invalid notes
                    if (data.notes && typeof data.notes === "object") {
                        data.notes = Object.fromEntries(
                            Object.entries(data.notes).filter(
                                ([id, note]) => note?.id && isValidUUID(note.id)
                            )
                        );
                    }

                    // Filter out any invalid folders
                    if (data.folders && typeof data.folders === "object") {
                        data.folders = Object.fromEntries(
                            Object.entries(data.folders).filter(
                                ([id, folder]) =>
                                    folder.id && isValidUUID(folder.id)
                            )
                        );
                    }

                    draft.data = { ...draft.data, ...data };
                })
            );

            // const userID = getUserAccount()?.id || "default";
            // saveDataStore(userID);
        },
    },
    add: {
        folder: (data) => {
            const newID = uuidv4();
            const newFolder: Folder = {
                id: newID,
                name: "New Folder",
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                color: 0,
                ...data,
            };

            set(
                produce((draft: Draft<DataStoreType>) => {
                    draft.data.folders[newID] = newFolder;
                })
            );

            const userID = getUserAccount()?.id || "default";
            saveDataStore(userID);
            return newID;
        },
        note: (data, openAfterCreate) => {
            const newID = data?.id || uuidv4();

            set(
                produce((draft: Draft<DataStoreType>) => {
                    data = data || {};
                    data.id = newID;

                    const newNote: Note = {
                        id: newID,
                        title: "New Note",
                        // content: "",
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                        tagIDs: [],
                        changesMade: false,
                        ...data,
                    };

                    draft.data.notes[newID] = newNote;
                })
            );

            if (openAfterCreate) {
                useMainStore.getState().open.openNote(newID);
            }

            const userID = getUserAccount()?.id || "default";
            saveDataStore(userID);

            return newID;
        },
        tag: (data) => {
            const newID = uuidv4();

            set(
                produce((draft: Draft<DataStoreType>) => {
                    data = data || {};
                    data.id = newID;

                    const newTag: Tag = {
                        id: data.id,
                        name: "New Tag",
                        dateCreated: new Date().toISOString(),
                        ...data,
                    };

                    draft.data.tags[data.id] = newTag;
                })
            );

            const userID = getUserAccount()?.id || "default";
            saveDataStore(userID);

            return newID;
        },
        duplicateNote(id, keepFolder, overrideData) {
            const newID = uuidv4();
            const noteData = get().data.notes[id];

            // Get the content of the text file to duplicate
            useTextFilesStore
                .getState()
                .getTextFile(id)
                .then((noteContent) => {
                    // Add the new text file
                    useTextFilesStore
                        .getState()
                        .saveContent(newID, noteContent?.content || "");

                    // Add the new note
                    get().add.note({
                        id: newID,
                        title: noteData.title,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                        tagIDs: noteData.tagIDs,
                        changesMade: false,
                        folderID: keepFolder ? noteData.folderID : undefined,
                        ...overrideData,
                    });
                });
        },
    },
    remove: {
        folder: (id) => {
            if (!get().data.folders[id]) return;

            // Remove all notes in the folder
            Object.values(get().data.notes).forEach((note) => {
                if (note.folderID !== id) return;

                get().set.note(note.id, {
                    folderID: undefined,
                });
            });

            // Remove the folder
            set(
                produce((draft: Draft<DataStoreType>) => {
                    delete draft.data.folders[id];

                    // Add to deleted
                    if (draft.data.deleted?.folders?.includes(id)) return;
                    draft.data.deleted.folders = [
                        ...draft.data.deleted.folders,
                        id,
                    ];
                })
            );

            useMainStore
                .getState()
                .syncFunctions.addChangesMade("tableRemoved");

            const userID = getUserAccount()?.id || "default";
            saveDataStore(userID);
        },
        note: (id) => {
            if (!get().data.notes[id]) return;
            useMainStore.getState().remove.closeNote(id);

            set(
                produce((draft: Draft<DataStoreType>) => {
                    // Remove any empty notes
                    Object.entries(draft.data.notes).forEach(([key, note]) => {
                        if (!note?.id) {
                            delete draft.data.notes[key];
                        }
                    });

                    delete draft.data.notes[id];

                    // Add to deleted
                    if (draft.data.deleted?.notes?.includes(id)) return;
                    draft.data.deleted.notes = [
                        ...draft.data.deleted.notes,
                        id,
                    ];
                })
            );

            useTextFilesStore.getState().removeTextFile(id);

            useMainStore
                .getState()
                .syncFunctions.addChangesMade("tableRemoved");

            const userID = getUserAccount()?.id || "default";
            saveDataStore(userID);
        },
        tag: (id) => {
            if (!get().data.tags[id]) return;

            set(
                produce((draft: Draft<DataStoreType>) => {
                    delete draft.data.tags[id];

                    // Add to deleted
                    if (draft.data.deleted?.tags?.includes(id)) return;
                    draft.data.deleted.tags = [...draft.data.deleted.tags, id];
                })
            );

            useMainStore
                .getState()
                .syncFunctions.addChangesMade("tableRemoved");

            const userID = getUserAccount()?.id || "default";
            saveDataStore(userID);
        },
    },
    initialize: () => {
        const userID = useAccountManagementStore.getState().userData?.id;
        set({ data: { ...dataStoreDefaultData } });
        saveDataStore(userID);
    },
}));

export default useDataStore;

// useDataStore.subscribe((state) => {
//     debouncedCallback(); // actually call the debounced function
// });

// const debouncedCallback = debounce(() => {
//     console.log("IS THIS USED?");

//     // console.log("Saved Main Store 2");
//     const userID = getUserAccount()?.id || "default";
//     saveDataStore(userID);
// }, 100);
